/**
 * Per-technician working hours (mirror of smartflow-api/utils/technicianWorkTime.js).
 *
 * member.planning_settings = {
 *   schedulable: boolean,                              // default true
 *   work_hours:  { "1".."7": { start, end } | null },  // ISO weekday; null = vrij,
 *                                                       // ontbreekt = organisatie-standaard
 *   lunch_break: { start, end } | null                 // ontbreekt = organisatie-standaard
 * }
 */

import { parseHmToMinutes, minutesToHm } from '@/utils/planningSlots';

export const WEEKDAY_KEYS = ['1', '2', '3', '4', '5', '6', '7'];
export const WEEKDAY_LABELS = {
  1: 'Maandag',
  2: 'Dinsdag',
  3: 'Woensdag',
  4: 'Donderdag',
  5: 'Vrijdag',
  6: 'Zaterdag',
  7: 'Zondag',
};
export const WEEKDAY_SHORT = { 1: 'ma', 2: 'di', 3: 'wo', 4: 'do', 5: 'vr', 6: 'za', 7: 'zo' };

/** ISO weekday key ('1' = maandag … '7' = zondag) for 'YYYY-MM-DD'. */
export function isoWeekdayKey(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getUTCDay();
  return String(day === 0 ? 7 : day);
}

function validRange(value) {
  if (!value || typeof value !== 'object') return null;
  const start = parseHmToMinutes(value.start);
  const end = parseHmToMinutes(value.end);
  if (start == null || end == null || end <= start) return null;
  return { start: minutesToHm(start), end: minutesToHm(end) };
}

/** Lenient read of a stored planning_settings value. */
export function getMemberPlanningSettings(raw) {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const workHours = {};
  if (src.work_hours && typeof src.work_hours === 'object') {
    for (const key of WEEKDAY_KEYS) {
      if (!(key in src.work_hours)) continue;
      const value = src.work_hours[key];
      if (value === null) {
        workHours[key] = null;
        continue;
      }
      const range = validRange(value);
      if (range) workHours[key] = range;
    }
  }
  const out = { schedulable: src.schedulable !== false, work_hours: workHours };
  if ('lunch_break' in src) {
    out.lunch_break = src.lunch_break === null ? null : validRange(src.lunch_break);
  }
  return out;
}

/** True when the member deviates from the organization defaults in any way. */
export function hasMemberOverrides(raw) {
  const s = getMemberPlanningSettings(raw);
  return !s.schedulable || Object.keys(s.work_hours).length > 0 || 'lunch_break' in s;
}

/**
 * Effective work time for one technician on one date.
 * @returns {{ workday_start, workday_end, lunch_break, break_minutes_between_jobs,
 *             timezone, day_off: boolean, source: 'member'|'org' }}
 */
export function resolveTechnicianWorkTime(orgWorkTime, memberPlanningSettings, dateStr) {
  const base = { ...(orgWorkTime || {}) };
  if (!base.lunch_break || !base.lunch_break.start || !base.lunch_break.end) {
    base.lunch_break = null;
  }
  const member = getMemberPlanningSettings(memberPlanningSettings);
  const key = isoWeekdayKey(dateStr);
  const result = { ...base, day_off: false, source: 'org' };

  if (key && key in member.work_hours) {
    const hours = member.work_hours[key];
    result.source = 'member';
    if (hours === null) {
      result.day_off = true;
      result.workday_end = result.workday_start;
    } else {
      result.workday_start = hours.start;
      result.workday_end = hours.end;
    }
  }
  if ('lunch_break' in member) {
    result.lunch_break = member.lunch_break;
    result.source = 'member';
  }
  return result;
}

/**
 * Compact human summary of a member's hours, e.g.
 * "ma–do 07:00–16:00 · vr 08:00–12:00 · za/zo vrij" or "Standaard werktijden".
 */
export function summarizeMemberHours(raw, orgWorkTime) {
  const s = getMemberPlanningSettings(raw);
  if (!s.schedulable) return 'Niet inplanbaar';
  const keys = Object.keys(s.work_hours);
  if (!keys.length && !('lunch_break' in s)) return 'Standaard werktijden';

  // Group consecutive weekdays with identical hours. Weekend days only show
  // up when the member explicitly works on them.
  const groups = [];
  for (const key of WEEKDAY_KEYS) {
    const value = key in s.work_hours ? s.work_hours[key] : 'org';
    const weekend = key === '6' || key === '7';
    if (weekend && (value === 'org' || value === null)) continue;
    const label =
      value === 'org'
        ? `${orgWorkTime?.workday_start || '08:00'}–${orgWorkTime?.workday_end || '17:00'}`
        : value === null
          ? 'vrij'
          : `${value.start}–${value.end}`;
    const last = groups[groups.length - 1];
    if (last && last.label === label && Number(last.to) === Number(key) - 1) {
      last.to = key;
    } else {
      groups.push({ from: key, to: key, label });
    }
  }
  const parts = groups.map((g) => {
    const days =
      g.from === g.to ? WEEKDAY_SHORT[g.from] : `${WEEKDAY_SHORT[g.from]}–${WEEKDAY_SHORT[g.to]}`;
    return `${days} ${g.label}`;
  });
  if ('lunch_break' in s) {
    parts.push(s.lunch_break ? `pauze ${s.lunch_break.start}–${s.lunch_break.end}` : 'geen pauze');
  }
  return parts.join(' · ');
}
